import { autocompleteClasses } from '@mui/material/Autocomplete';
import { inputAdornmentClasses } from '@mui/material/InputAdornment';
import { inputBaseClasses } from '@mui/material/InputBase';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import shadows from '@mui/material/styles/shadows';
import type { Shadows } from '@mui/material/styles/shadows';
import type React from 'react';
import type { PropsWithChildren } from 'react';

import './global.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { nativeSelectClasses } from '@mui/material/NativeSelect';
import { typographyClasses } from '@mui/material/Typography';

export const theme = createTheme({
  shadows: shadows.map(() => 'none') as Shadows,
  palette: {
    primary: {
      main: '#171717',
      light: '#2e2e2e',
      dark: '#0d0d0d',
      contrastText: '#fff',
    },
    secondary: {
      main: '#ffffff',
      light: '#f5f5f5',
      dark: '#e0e0e0',
      contrastText: '#171717',
    },
  },
  // typography: {
  //   fontSize: 12,
  // },
  components: {
    // biome-ignore lint/style/useNamingConvention: <explanation>
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          minWidth: 'unset',
          ...(ownerState.variant === 'text' && {
            '&:hover': {
              backgroundColor: 'unset',
            },
          }),
        }),
      },
      defaultProps: {
        disableRipple: true,
      },
    },
    // biome-ignore lint/style/useNamingConvention: <explanation>
    MuiMenuItem: {
      defaultProps: {
        disableRipple: true,
      },
    },
    // biome-ignore lint/style/useNamingConvention: <explanation>
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: '1.25rem',
          fontWeight: 'bold',
        },
      },
    },
    // biome-ignore lint/style/useNamingConvention: <explanation>
    MuiFormControlLabel: {
      styleOverrides: {
        root: () => ({
          marginLeft: 0,
          [`& .${typographyClasses.root}`]: {
            // fontSize: '0.75rem',
          },
        }),
      },
    },
    // biome-ignore lint/style/useNamingConvention: <explanation>
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
            {
              display: 'none',
            },
          '& input[type=number]': {
            // biome-ignore lint/style/useNamingConvention: <explanation>
            MozAppearance: 'textfield',
          },
          [`& .${inputBaseClasses.root}.${inputBaseClasses.disabled}`]: {
            backgroundColor: theme.palette.secondary.main,
          },
          // NOTE: TextAreaの規定スタイル
          [`& .${inputBaseClasses.multiline}`]: {
            padding: 0,
          },
          // NOTE: TextFieldとTextAreaで共通のスタイル
          [`& .${inputBaseClasses.input}.${inputBaseClasses.inputSizeSmall}`]: {
            height: 20,
            // fontSize: '0.75rem',
            padding: theme.spacing(0.5, 1),
          },
          // NOTE: inputAdornmentを持っている場合
          [`& .${inputBaseClasses.root} .${inputAdornmentClasses.root} .${typographyClasses.root}`]:
            {
              marginRight: '-14px',
            },
          // NOTE: sizeがsmallの場合はinputAdornmentの文字サイズを小さくする
          [`&:has(.${inputBaseClasses.input}.${inputBaseClasses.inputSizeSmall}) .${inputAdornmentClasses.root} .${typographyClasses.root}`]:
            {
              // fontSize: '0.7rem',
            },
        }),
      },
    },
    // biome-ignore lint/style/useNamingConvention: <explanation>
    MuiSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          [`&.${inputBaseClasses.inputSizeSmall}`]: {
            height: 20,
            // fontSize: '0.75rem',
            padding: theme.spacing(0.5, 1),
          },
        }),
      },
    },
    // biome-ignore lint/style/useNamingConvention: <explanation>
    MuiNativeSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          [`&.${nativeSelectClasses.multiple}`]: {
            overflowY: 'auto',
            height: 'auto',
            '&:hover': {
              borderColor: theme.palette.secondary.main,
            },
            [`&.${inputBaseClasses.inputSizeSmall}`]: {
              padding: theme.spacing(0.5),
              // fontSize: '0.75rem',
            },
          },
        }),
      },
    },
    // biome-ignore lint/style/useNamingConvention: <explanation>
    MuiAutocomplete: {
      styleOverrides: {
        root: ({ theme }) => ({
          [`& .${autocompleteClasses.inputRoot}.${inputBaseClasses.root}.${inputBaseClasses.sizeSmall}`]:
            {
              padding: 0,
              [`& .${autocompleteClasses.input}`]: {
                padding: theme.spacing(0.5, 1),
                // fontSize: '0.75rem',
              },
            },
        }),
        popper: ({ theme }) => ({
          // TODO: 色をTextFieldのoutlineと合わせたい
          border: `1px solid ${theme.palette.secondary.main}`,
          [`& .${autocompleteClasses.listbox}`]: {
            // fontSize: '0.7rem',
          },
        }),
      },
    },
  },
});

export const ThemeProviderWrap: React.FC<PropsWithChildren> = ({
  children,
}) => <ThemeProvider theme={theme}>{children}</ThemeProvider>;
